import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  getFacilityDashboard,
  type FacilityIndividualDashboardResponse,
} from "@services/facilityDashboard";
import {
  facilityComplianceService,
  type ComplianceCalendarItem,
  type FacilityCompliancePayload,
  type PenaltyItem,
} from "@services/facilityCompliance";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { getCurrentUserId } from "@utils/userUtils";
import FacilityDashboardTab from "../FacilityDashboardTab";
import FacilityNetworkBackButton from "./FacilityNetworkBackButton";
import { FN } from "./facilityNetworkTheme";

type Props = {
  facility: MyFacilityListRow;
  onBack: () => void;
};

function buildNetworkChildCompliancePayload(
  facility: MyFacilityListRow,
  userId: number,
): FacilityCompliancePayload {
  const profileTargeting =
    facility.profile_targeting_json || facility.aviation_targeting || null;
  return {
    user_id: userId,
    organization: facility.facility_name || "",
    facility_name: facility.facility_name || "",
    facility_type:
      facility.primary_profile_slug ||
      facility.facility_type_slug ||
      facility.facility_type ||
      "",
    country: getSelectedCountryCode(),
    state: facility.state ? String(facility.state).trim() : "",
    county: "",
    city: facility.city ? String(facility.city).trim() : "",
    site_url: "",
    documents: "",
    as_of_date: new Date().toISOString().split("T")[0],
    response_language: "English",
    instructions:
      "Search government regulatory sites for compliance requirements, permits, licenses, and medical facility documentation",
    output_format: "JSON",
    org_name: facility.org_name || "test",
    last_medicare_revalidation_date:
      facility.last_medicare_revalidation_date ?? undefined,
    last_medicaid_revalidation_date:
      facility.last_medicaid_revalidation_date ?? undefined,
    facility_id: facility.facility_id,
    facility_type_id:
      facility.vertical === "aviation"
        ? undefined
        : (facility as { facility_type_id?: number }).facility_type_id,
    vertical: facility.vertical ?? undefined,
    primary_profile_slug: facility.primary_profile_slug ?? undefined,
    profile_targeting_json: profileTargeting,
    activity_domains:
      facility.activity_domains ??
      (profileTargeting?.activity_domains as string[] | undefined),
    operation_contexts:
      facility.operation_contexts ??
      (profileTargeting?.operation_contexts as string[] | undefined),
    asset_types:
      facility.asset_types ??
      (profileTargeting?.asset_types as string[] | undefined),
    certificates_or_approvals:
      facility.certificates_or_approvals ??
      (profileTargeting?.certificates_or_approvals as string[] | undefined),
    regulation_packs:
      facility.regulation_packs ??
      (profileTargeting?.regulation_packs as string[] | undefined),
    overlay_packs:
      facility.overlay_packs ??
      (profileTargeting?.overlay_packs as string[] | undefined),
    penalty_packs:
      facility.penalty_packs ??
      (profileTargeting?.penalty_packs as string[] | undefined),
    include_penalties: false,
    ...(() => {
      const v = (
        facility as { show_tax_compliance_calendar?: unknown }
      ).show_tax_compliance_calendar;
      if (v === true || v === false) {
        return { show_tax_compliance_calendar: v };
      }
      return {};
    })(),
  };
}

/**
 * Parent views a linked child individual dashboard inside Facility Network.
 * Child need not appear in My Facilities — access is via active parent-child link.
 */
export default function FacilityNetworkChildDashboardView({ facility, onBack }: Props) {
  const [data, setData] = useState<FacilityIndividualDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [complianceCalendar, setComplianceCalendar] = useState<
    ComplianceCalendarItem[] | null
  >(null);
  const [potentialPenaltyItems, setPotentialPenaltyItems] = useState<
    PenaltyItem[] | null
  >(null);
  const [asOfDate, setAsOfDate] = useState<string | null>(null);
  const [complianceCalendarLoading, setComplianceCalendarLoading] = useState(true);

  const load = React.useCallback(() => {
    const userId = Number(getCurrentUserId());
    if (!userId || !facility.facility_id) {
      setError("Unable to load dashboard.");
      setLoading(false);
      setComplianceCalendarLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setComplianceCalendarLoading(true);

    const dashboardPromise = getFacilityDashboard(
      facility.facility_id,
      userId,
      getSelectedCountryCode(),
    )
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        setData(null);
        setError(err instanceof Error ? err.message : "Failed to load dashboard.");
      })
      .finally(() => setLoading(false));

    const penaltiesPromise = facilityComplianceService
      .getFacilityCompliancePenaltiesOnly(
        buildNetworkChildCompliancePayload(facility, userId),
      )
      .then((penalties) => {
        setComplianceCalendar(
          penalties.sections?.penaltiesAndTotalExposure?.complianceCalendar ?? [],
        );
        setPotentialPenaltyItems(
          penalties.sections?.penaltiesAndTotalExposure?.items ?? [],
        );
        setAsOfDate(penalties.entityDetails?.asOfDate ?? null);
      })
      .catch(() => {
        // Calendar badges optional — still show BFF strip if penalties fail
        setComplianceCalendar(null);
        setPotentialPenaltyItems(null);
        setAsOfDate(null);
      })
      .finally(() => setComplianceCalendarLoading(false));

    void Promise.all([dashboardPromise, penaltiesPromise]);
  }, [facility]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <FacilityNetworkBackButton
        label="Back to Linked facilities"
        variant="link"
        onClick={onBack}
      />
      <Box>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 18,
            fontWeight: 600,
            color: FN.pressed,
          }}
        >
          {facility.facility_name}
        </Typography>
        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.muted }}>
          Linked facility dashboard
          {facility.city || facility.state
            ? ` · ${[facility.city, facility.state].filter(Boolean).join(", ")}`
            : ""}
        </Typography>
      </Box>

      {loading && !data ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: FN.primary }} />
        </Box>
      ) : error && !data ? (
        <Alert
          severity="error"
          action={
            <Box
              component="button"
              type="button"
              onClick={load}
              sx={{
                border: "none",
                bgcolor: "transparent",
                color: FN.primary,
                cursor: "pointer",
                fontWeight: 600,
                fontFamily: FN.fontFamily,
              }}
            >
              Retry
            </Box>
          }
        >
          {error}
        </Alert>
      ) : (
        <FacilityDashboardTab
          facility={facility}
          isTabActive
          data={data}
          loading={loading}
          error={error}
          onRetry={load}
          onNavigate={() => {
            /* Stay in network — no jump to My Facility tabs */
          }}
          hideExternalNavigation
          complianceCalendar={complianceCalendar}
          potentialPenaltyItems={potentialPenaltyItems}
          asOfDate={asOfDate}
          complianceCalendarLoading={complianceCalendarLoading}
        />
      )}
    </Box>
  );
}
